import React, { useState, useCallback, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';

import ComponentLoader from '../components/shared/ComponentLoader';
import { 
  CanvasWrapper, 
  Canvas, 
  GridOverlay,
  ComponentWrapper,
  DragHandle,
  RemoveButton,
  CanvasContainer,
  ResizeHandle
} from './styles';

// CanvasControls removed - now handled by builder
// Dynamic imports for better performance
const LiveScore = lazy(() => import('../components/LiveScore'));
const Scoreboard = lazy(() => import('../components/Scoreboard'));
const Events = lazy(() => import('../components/Events'));
const Statistics = lazy(() => import('../components/Statistics'));
const Formation = lazy(() => import('../components/Formation'));
const HeadToHead = lazy(() => import('../components/HeadToHead'));
const Standings = lazy(() => import('../components/Standings'));
const MatchInfo = lazy(() => import('../components/MatchInfo'));
const TeamLineup = lazy(() => import('../components/TeamLineup'));

// Component types that can be added to canvas
const COMPONENT_TYPES = {
  LIVE_SCORE: 'LiveScore',
  SCOREBOARD: 'Scoreboard',
  EVENTS: 'Events',
  STATISTICS: 'Statistics',
  FORMATION: 'Formation',
  HEAD_TO_HEAD: 'HeadToHead',
  STANDINGS: 'Standings',
  MATCH_INFO: 'MatchInfo',
  TEAM_LINEUP: 'TeamLineup',
};

// Component defaults moved to builder - no longer needed here

// Component mapping
const COMPONENT_MAP = {
  [COMPONENT_TYPES.LIVE_SCORE]: LiveScore,
  [COMPONENT_TYPES.SCOREBOARD]: Scoreboard,
  [COMPONENT_TYPES.EVENTS]: Events,
  [COMPONENT_TYPES.STATISTICS]: Statistics,
  [COMPONENT_TYPES.FORMATION]: Formation,
  [COMPONENT_TYPES.HEAD_TO_HEAD]: HeadToHead,
  [COMPONENT_TYPES.STANDINGS]: Standings,
  [COMPONENT_TYPES.MATCH_INFO]: MatchInfo,
  [COMPONENT_TYPES.TEAM_LINEUP]: TeamLineup,
};

// Minimum dimensions for each component type
const COMPONENT_MIN_SIZES = {
  [COMPONENT_TYPES.LIVE_SCORE]: { width: 430, height: 120 },
  [COMPONENT_TYPES.SCOREBOARD]: { width: 300, height: 150 },
  [COMPONENT_TYPES.EVENTS]: { width: 350, height: 200 },
  [COMPONENT_TYPES.STATISTICS]: { width: 400, height: 600 },
  [COMPONENT_TYPES.FORMATION]: { width: 300, height: 480 },
  [COMPONENT_TYPES.HEAD_TO_HEAD]: { width: 350, height: 180 },
  [COMPONENT_TYPES.STANDINGS]: { width: 450, height: 300 },
  [COMPONENT_TYPES.MATCH_INFO]: { width: 280, height: 400 },
  [COMPONENT_TYPES.TEAM_LINEUP]: { width: 300, height: 450 },
};

function SoccerProCanvas({ 
  matchData, 
  canvasConfig, 
  onConfigChange, 
  onComponentSelect, 
  onUpdateComponent, 
  onDeleteComponent, 
  selectedComponent, 
  builderMode = false,
  theme = {}
}) {
  const components = canvasConfig?.components || [];
  const showGrid = canvasConfig?.grid !== false;
  const zoom = canvasConfig?.zoom || 1;
  const [localSelectedComponent, setLocalSelectedComponent] = useState(null);
  const currentSelectedComponent = selectedComponent || localSelectedComponent;

  // Add new component to canvas - now handled by builder
  // This function is no longer needed as the builder handles adding components

  // Update component position during drag
  const handleDrag = useCallback((e, data, componentId) => {
    // Real-time position updates could be added here if needed
  }, []);

  // Save position after drag ends
  const handleDragStop = useCallback((e, data, componentId) => {
    if (onUpdateComponent) {
      onUpdateComponent(componentId, {
        position: { x: data.x, y: data.y }
      });
    }
  }, [onUpdateComponent]);

  // Remove component from canvas
  const handleRemoveComponent = useCallback((componentId) => {
    if (onDeleteComponent) {
      onDeleteComponent(componentId);
    }
  }, [onDeleteComponent]);


  // Handle component resize during resize
  const handleResize = useCallback((e, { size }, componentId) => {
    // Real-time size updates could be added here if needed
  }, []);

  // Render the actual component with Suspense for lazy loading
  const renderComponent = (component) => {
    const ComponentToRender = COMPONENT_MAP[component.type];
    if (!ComponentToRender) {
      return <div>Unknown component: {component.type}</div>;
    }

    // Determine team index from team property
    const teamIndex = component.team === 'away' ? 1 : 0;

    return (
      <Suspense fallback={<ComponentLoader text={`Loading ${component.type}...`} />}>
        <ComponentToRender
          data={matchData}
          teamIndex={teamIndex}
          styles={theme}
        />
      </Suspense>
    );
  };

  // Save size after resize ends
  const handleResizeStop = useCallback((e, { size }, componentId) => {
    if (onUpdateComponent) {
      onUpdateComponent(componentId, {
        size: { width: size.width, height: size.height }
      });
    }
  }, [onUpdateComponent]);

  // Calculate max constraints based on component position and canvas bounds
  const getMaxConstraints = useCallback((component) => {
    const canvasWidth = canvasConfig?.width || 1920;
    const canvasHeight = canvasConfig?.height || 1080;
    
    const maxWidth = canvasWidth - component.position.x;
    const maxHeight = canvasHeight - component.position.y;
    
    return [
      Math.max(COMPONENT_MIN_SIZES[component.type]?.width || 200, maxWidth),
      Math.max(COMPONENT_MIN_SIZES[component.type]?.height || 150, maxHeight)
    ];
  }, [canvasConfig]);

  return (
    <CanvasWrapper style={{ 
      background: builderMode ? '#1a1a1a' : 'transparent',
      overflow: builderMode ? 'visible' : 'hidden'
    }}>
      {/* Component palette is now handled by the builder */}
      
      <CanvasContainer style={{ 
        padding: builderMode ? '40px' : '0',
        alignItems: builderMode ? 'center' : 'flex-start',
        justifyContent: builderMode ? 'center' : 'flex-start',
        overflow: builderMode ? 'visible' : 'hidden'
      }}>
        <Canvas 
          style={{ 
            transform: builderMode ? `scale(${zoom})` : 'none',
            transformOrigin: builderMode ? 'center center' : 'unset',
            background: builderMode ? '#000' : 'transparent',
            boxShadow: builderMode ? '0 0 20px rgba(0, 0, 0, 0.5)' : 'none',
            margin: builderMode ? 'auto' : '0',
            width: canvasConfig?.width || 1920,
            height: canvasConfig?.height || 1080,
            overflow: builderMode ? 'hidden' : 'visible'
          }}
        >
          {showGrid && builderMode && <GridOverlay />}
          
          {components.map((component) => (
            <Draggable
              key={component.id}
              position={component.position}
              disabled={!builderMode}
              onDrag={(e, data) => handleDrag(e, data, component.id)}
              onStop={(e, data) => handleDragStop(e, data, component.id)}
              bounds={{
                left: 0,
                top: 0,
                right: 1920 - component.size.width,
                bottom: 1080 - component.size.height
              }}
              handle=".drag-handle"
              grid={showGrid ? [10, 10] : null}
            >
              <div style={{ position: 'absolute' }}>
                {builderMode ? (
                  <ResizableBox
                    width={component.size.width}
                    height={component.size.height}
                    minConstraints={[
                      COMPONENT_MIN_SIZES[component.type]?.width || 200,
                      COMPONENT_MIN_SIZES[component.type]?.height || 150
                    ]}
                    maxConstraints={getMaxConstraints(component)}
                    onResize={(e, data) => handleResize(e, data, component.id)}
                    onResizeStop={(e, data) => handleResizeStop(e, data, component.id)}
                    handle={builderMode ? <ResizeHandle /> : null}
                    resizeHandles={builderMode ? ['se'] : []}
                    axis="both"
                  >
                    <ComponentWrapper
                      style={{
                        width: '100%',
                        height: '100%',
                        opacity: component.visible ? 1 : 0.5,
                        border: builderMode ? (currentSelectedComponent === component.id ? '2px solid #4CAF50' : '2px solid #444') : 'none',
                        background: builderMode ? 'rgba(30, 30, 30, 0.9)' : 'transparent',
                        position: 'relative',
                        overflow: 'hidden',
                        padding: 0
                      }}
                      onClick={builderMode ? () => {
                        setLocalSelectedComponent(component.id);
                        if (onComponentSelect) {
                          onComponentSelect(component);
                        }
                      } : undefined}
                    >
                      {/* Floating buttons - only in builder mode */}
                      {builderMode && (
                        <>
                          <DragHandle className="drag-handle" style={{ 
                            position: 'absolute',
                            top: '5px',
                            left: '5px',
                            pointerEvents: 'auto', 
                            zIndex: 10 
                          }} />
                          <RemoveButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveComponent(component.id);
                            }}
                            style={{ 
                              position: 'absolute',
                              top: '5px',
                              right: '5px',
                              pointerEvents: 'auto', 
                              zIndex: 10 
                            }}
                          >
                            ×
                          </RemoveButton>
                        </>
                      )}
                      
                      {/* Component content fills entire space */}
                      <div style={{ 
                        pointerEvents: builderMode ? 'none' : 'auto',
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        overflow: 'hidden'
                      }}>
                        {renderComponent(component)}
                      </div>
                      
                      {/* Component type label */}
                      {builderMode && (
                        <div style={{ 
                          position: 'absolute',
                          top: '5px',
                          right: '30px',
                          background: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          zIndex: 10,
                          pointerEvents: 'none'
                        }}>
                          {component.type}
                        </div>
                      )}
                    </ComponentWrapper>
                  </ResizableBox>
                ) : (
                  <div
                    style={{
                      width: component.size.width,
                      height: component.size.height,
                      opacity: component.visible ? 1 : 0.5,
                      pointerEvents: 'none'
                    }}
                  >
                    {renderComponent(component)}
                  </div>
                )}
              </div>
            </Draggable>
          ))}
        </Canvas>
      </CanvasContainer>
      
      {/* Canvas controls are now handled by the builder */}
    </CanvasWrapper>
  );
}

SoccerProCanvas.propTypes = {
  matchData: PropTypes.object,
  canvasConfig: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    grid: PropTypes.bool,
    zoom: PropTypes.number,
    components: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        position: PropTypes.shape({
          x: PropTypes.number.isRequired,
          y: PropTypes.number.isRequired,
        }).isRequired,
        size: PropTypes.shape({
          width: PropTypes.number.isRequired,
          height: PropTypes.number.isRequired,
        }).isRequired,
        visible: PropTypes.bool,
      })
    ),
  }),
  onConfigChange: PropTypes.func,
  onComponentSelect: PropTypes.func,
  onUpdateComponent: PropTypes.func,
  onDeleteComponent: PropTypes.func,
  selectedComponent: PropTypes.object,
  builderMode: PropTypes.bool,
};

SoccerProCanvas.defaultProps = {
  matchData: null,
  canvasConfig: {
    width: 1920,
    height: 1080,
    grid: true,
    zoom: 1,
    components: [],
  },
  onConfigChange: null,
  onComponentSelect: null,
  onUpdateComponent: null,
  onDeleteComponent: null,
  selectedComponent: null,
  builderMode: false,
};

export default SoccerProCanvas;